import { UnknownError } from '@omnea-digital/toolbox'
import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import range from 'lodash/range'
import uniqBy from 'lodash/uniqBy'
import { getPollingGCTime } from '@omnea-app/fe-api-client/queryConfig'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { FilterModel } from '@omnea-app/packages/universal/saved-filters/validateFilterModel'
import { isUuid } from '@omnea-app/packages/universal/string-utils/isUuid'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { RequestTopLevelFilterKey, RequestDetail, ListRequestsBody } from '@omnea-app/requests/requestsFrontendClient'
import { RequestListItem, RequestResponse, RequestsHttpClient } from '@omnea-app/requests/requestsHttpClient'

export const RequestDetailQueryKey = 'GetRequestDetail'

export type GetRequestResponse = RequestResponse<'/{id}', 'get'>

export const fetchRequestDetails = async (token: string, requestId: string): Promise<RequestDetail> => {
  const apiClient = new RequestsHttpClient({ authorization: token })

  const { data, error } = await apiClient.GET('/{id}', {
    params: { path: { id: requestId } },
  })

  throwIfApiError(error)
  return data as RequestDetail
}

export const useGetRequestDetail = <TData = GetRequestResponse['data']>(
  requestId: string | null,
  options?: Omit<
    UseQueryOptions<GetRequestResponse['data'], GetRequestResponse['error'], TData>,
    'queryKey' | 'gcTime' | 'queryFn' | 'staleTime' | 'refetchInterval'
  > & { refetchInterval?: number | false }
) => {
  const { getToken } = useAuthenticatedUser()

  return useQuery<GetRequestResponse['data'], GetRequestResponse['error'], TData>({
    ...options,
    queryKey: [RequestDetailQueryKey, requestId],
    gcTime: getPollingGCTime(options?.refetchInterval),
    queryFn: async () => {
      if (!isDefined(requestId)) throw new UnknownError({ message: 'No requestID provided in fetch' })

      return await fetchRequestDetails(await getToken(), requestId)
    },
    // We call this hook from a few different places, so want to make sure that we don't
    // fire off a request for every single caller if the first one has already returned
    staleTime: 5_000,
  })
}
export const useRequestDetail = (
  requestId: string | null,
  { refetchInterval, skip }: { refetchInterval?: number | false; skip?: boolean } = {}
) => {
  const { user } = useAuthenticatedUser()

  return useGetRequestDetail(requestId, {
    refetchInterval,
    enabled: !user.isSupplier && isUuid(requestId) && !skip,
  }) as UseQueryResult<RequestDetail, Error>
}
export const RequestDetailsQueryKey = 'GetRequestDetails'

type TopLevelFilter = 'all' | 'pending' | 'rejected-tasks' | 'errored-tasks'

const batchTypeByTopLevelFilter: Record<RequestTopLevelFilterKey, TopLevelFilter> = {
  all: 'all',
  completed: 'all',
  rejected: 'all',
  'rejected-tasks': 'rejected-tasks',
  'errored-tasks': 'errored-tasks',
  cancelled: 'all',
  mineCompleted: 'all',
  mineCancelled: 'all',
  mine: 'all',
  following: 'all',
  pending: 'pending',
  minePending: 'pending',
  delayed: 'pending',
  mineDelayed: 'pending',
  owned: 'all',
  paused: 'all',
  minePaused: 'all',
  'awaiting-receipt': 'all',
}

export const useAllRequests = (requestsBody?: ListRequestsBody, supplierId?: string) => {
  const { getToken } = useAuthenticatedUser()

  const batchesTopLevelFilter = batchTypeByTopLevelFilter[requestsBody?.topLevelFilter || 'all']

  const requests = useQuery<RequestListItem[], Error>({
    queryKey: [RequestDetailsQueryKey, supplierId, batchesTopLevelFilter],
    queryFn: async () => {
      const authorization = await getToken()
      const requestsClient = new RequestsHttpClient({ authorization })
      return fetchRequestsInBatches(requestsClient, batchesTopLevelFilter, requestsBody?.filterModel || null)
    },
  })

  return requests
}

const recordsPerBatch = 50

const fetchRequestsInBatches = async (
  requestsClient: RequestsHttpClient,
  batchesTopLevelFilter: TopLevelFilter,
  filterModel: FilterModel | null
) => {
  const count = await getRequestCount(requestsClient, batchesTopLevelFilter, filterModel)

  if (!count || count.totalCount === 0) return []
  const totalPages = Math.ceil(count.totalCount / recordsPerBatch)

  // All pages starting from index 1 (page 2)
  const pagesToLoad = range(0, totalPages)

  const requests = await Promise.all(
    pagesToLoad.map((page) => fetchBatch(requestsClient, batchesTopLevelFilter, page, filterModel))
  )
  const allRequests = requests
    .map((data) => data?.requests)
    .flat()
    .filter(Boolean) as RequestListItem[]

  // remove duplicates between batches just in case
  return uniqBy(allRequests, 'rawData.id')
}

const getRequestCount = async (
  requestsClient: RequestsHttpClient,
  topLevelFilter: TopLevelFilter,
  filterModel: FilterModel | null
) => {
  const { data } = await requestsClient.POST('/paginated', {
    body: {
      topLevelFilter,
      paginationModel: { page: 0, pageSize: 0 },
      filterModel,
      sorting: null,
    },
  })

  if (data && 'cantPaginate' in data) throw new Error('cant fetch requests')

  return data
}

const fetchBatch = async (
  requestsClient: RequestsHttpClient,
  topLevelFilter: TopLevelFilter,
  page: number,
  filterModel: FilterModel | null
) => {
  const { data } = await requestsClient.POST('/paginated', {
    body: {
      topLevelFilter,
      paginationModel: { page, pageSize: recordsPerBatch },
      filterModel,
      sorting: null,
    },
  })

  // Should never happen, because top level filter is set to 'pending' or 'all' with no other filters or sorting
  // The backend can always handle this request
  if (data && 'cantPaginate' in data) {
    throw new Error('cant fetch requests')
  }

  return data
}
